import { loadAssayData, loadAssayDatasets } from '@/utils/api/dataManagement'

function assaySampleTypeFilter(assayType) {
  switch (assayType) {
    case 'AVITISEQUENCINGASSAY':
    case 'NANOPOREASSAY':
    case 'NGSILLUMINAASSAY':
      return ['SEQUENCINGLIBRARY']
    case 'VOLUMEEMASSAY':
    case 'TRANSMISSIONEMASSAY':
      return ['EMSAMPLE']
    case 'LIGHTMICROSCOPYSCREENASSAY':
      return ['GENERICSAMPLE', 'EMSAMPLE']
    case 'LIGHTMICROSCOPYASSAY':
    case 'GENERICASSAY':
    case null:
    case '':
    default:
      return []
  }
}

function defaultState() {
  return {
    assayData: {},
    assayDatasetsData: [],
    assayType: null,
    readOnlyAssay: false,
    registerToAssay: false,
    sampleType: 'GENERICSAMPLE',
    templateData: {},
  }
}

const state = {
  ...defaultState(),
}

const getters = {
  assayId: (state) => state.assayData?.id?.value || null,
  assayRunDir: (state) => state.assayData?.run_dir?.value || null,
  assayTechnology: (state) => state.assayData?.technology?.value || null,
  assayName: (state) => state.assayData?.name?.value?.name || '',
  assayIsManaged: (state) => state.assayData?.is_managed?.value || false,
  assayRunType: (state) => state.assayData?.runtype?.value?.value || '',
  assayIsRegistered: (_, getters) => getters.assayState === 'registered',
  assayIsIncoming: (_, getters) => getters.assayState === 'incoming',
  assayIsInitialized: (_, getters) => getters.assayState === 'initialized',
  assayIsNew: (_, getters) => getters.assayState === 'new',
  /** The canonical assay type for the current wizard session.
   *  Priority: saved assay (navigate-back) > template > user selection.
   *  All sources are normalised to UPPERCASE for consistent comparison. */
  resolvedAssayType: (state) => {
    return (
      state.assayData?.type?.value?.name?.toUpperCase() ||
      state.templateData?.type?.value?.name?.toUpperCase() ||
      state.assayType ||
      null
    )
  },
  assayState: (state) =>
    state.assayData?.state?.value?.value?.toLowerCase() || null,
  // run_dir is undefined when no assay is loaded yet, null when the assay
  // exists but has no run directory assigned.  Both mean "no run dir".
  assayHasRunDir: (state) => {
    const runDir = state.assayData?.run_dir?.value
    return runDir !== undefined && runDir !== null
  },
  assayHasRegisteredData: (state) => {
    return state.assayDatasetsData.length > 0
  },
  assayHasRegisteredRawData: (state) => {
    return state.assayDatasetsData.some((d) => d.is_raw.value === true)
  },
  enforceRawData: (state, getters) => {
    /* In case no raw data has been registered with this assay, we enforce
     * the user to registered at least a collection of raw data */
    return getters.assayId && !getters.assayHasRegisteredRawData
  },
  /** Flatten assayData by unwrapping the `value` layer from each field.
   *  `name` and `type` are extracted as their inner `.name` string. */
  flatAssayData: (state) => {
    const flat = {}
    for (const [key, field] of Object.entries(state.assayData)) {
      flat[key] =
        key === 'name' || key === 'type' ? field.value?.name : field.value
    }
    return flat
  },
  assayBaseCollectionData: (_, getters) => {
    let name = `${getters.assayName}`
    if (getters.enforceRawData) name += ' raw'
    const isPairedEnd = getters.assayRunType === 'paired-end'
    const pairedEndData = {
      dimensionsId: 'readtype',
      dimensionsValues: ['read_1', 'read_2'],
    }
    return {
      name,
      isRawData: getters.enforceRawData,
      ...((isPairedEnd && pairedEndData) || {}),
    }
  },
  transferableTemplateData: (state) => {
    const nonTransferableAttrKeys = [
      'id',
      'state',
      'flowcell',
      'created',
      'modified',
      'owner',
      'owned_by',
      'responsible_person',
      'permissions',
      'datasessions',
      'is_template',
      'run_dir',
      'deleted',
      'deleted_by',
      'deleted_date',
    ]
    const templateDataKeys = Object.keys(state.templateData || {})
    const transferableAttrKeys = templateDataKeys.filter(
      (k) => !nonTransferableAttrKeys.includes(k),
    )

    const templateData = {}
    if (templateDataKeys.length) {
      templateData.name = {
        value: {
          name: `New assay (template:${state.templateData?.name?.value?.name})`,
        },
      }
    }
    for (const k of transferableAttrKeys) {
      templateData[k] = state.templateData[k]
    }
    // Cleanup the protocol_list object so that server does not
    // complain about the existing protocol_list UUID
    const protocolList = templateData?.protocol_list?.value?.protocols
    if (protocolList)
      templateData.protocol_list.value = { protocols: protocolList }
    return templateData
  },
  getSampleTypeOptions: (state, getters, rootState, rootGetters) => {
    // Get all sample types
    const getMetaModelTypes = rootGetters['meta/getMetaModelTypes']
    const sampleMetaTypes = getMetaModelTypes('stocks', 'sample')
    return Object.keys(sampleMetaTypes)
      .filter((t) => t.toLowerCase() !== 'total')
      .map((sampleType) => {
        return {
          id: sampleType,
          ...sampleMetaTypes[sampleType],
        }
      })
      .sort((a, b) => (a.menu_order ?? 0) - (b.menu_order ?? 0))
  },
  getAssaySampleTypeOptions: (state, getters) => {
    // Get sample types specific to the current assay type
    const sampleTypeOptions = getters.getSampleTypeOptions
    let assaySampleTypesOptions = sampleTypeOptions
    const filters = assaySampleTypeFilter(state.assayType)
    if (filters.length >= 1)
      assaySampleTypesOptions = sampleTypeOptions.filter((o) =>
        filters.includes(o.id),
      )
    return assaySampleTypesOptions
  },
  sampleTypeModel: (state, getters) => {
    return getters.getSampleTypeOptions?.find((o) => o.id === state.sampleType)
  },
}
const actions = {
  async reset({ commit }) {
    commit('RESET')
  },
  async setAssayData({ commit, getters, dispatch }, id) {
    let assayData = {}
    // Skip re-fetching if the store already holds data for this assay
    if (getters.assayId !== id) {
      if (id) assayData = await loadAssayData(id)
      commit('SET_ASSAY_DATA', assayData)
      const assayType = getters.resolvedAssayType
      dispatch('setAssayType', assayType)
      const assayDatasetsData = await loadAssayDatasets(id)
      commit('SET_ASSAY_DATASETS_DATA', assayDatasetsData)
    }
  },
  setAssayType({ commit, getters }, assayType) {
    assayType = assayType?.toUpperCase() || null
    commit('SET_ASSAY_TYPE', assayType)
    const assaySampleTypes = getters.getAssaySampleTypeOptions
    const sampleType = assaySampleTypes[0]?.id
    commit('SET_SAMPLE_TYPE', sampleType)
  },
  async setTemplateData({ commit }, id) {
    let assayData = {}
    if (id) assayData = await loadAssayData(id)
    commit('SET_TEMPLATE_DATA', assayData)
  },
}
const mutations = {
  RESET(state) {
    Object.assign(state, { ...defaultState() })
  },
  SET_REGISTER_TO_ASSAY(state, registerToAssay) {
    state.registerToAssay = registerToAssay
  },
  SET_READONLY_ASSAY(state, readOnlyAssay) {
    state.readOnlyAssay = readOnlyAssay
  },
  SET_ASSAY_TYPE(state, assayType) {
    assayType = assayType?.toUpperCase() || null
    state.assayType = assayType
  },
  SET_SAMPLE_TYPE(state, sampleType) {
    state.sampleType = sampleType
  },
  SET_TEMPLATE_DATA(state, data) {
    state.templateData = data
  },
  SET_ASSAY_DATA(state, data) {
    state.assayData = data
  },
  SET_ASSAY_DATASETS_DATA(state, data) {
    state.assayDatasetsData = data
  },
  RESET_TEMPLATE_DATA(state) {
    state.templateData = {}
  },
  RESET_ASSAY_DATA(state) {
    state.assayData = {}
  },
}

const assay = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
}

export default assay
